import { Component } from '@theme/component';

/**
 * Product gallery: a 2-column grid shows every image at once, each its own
 * `.ppz-gallery-main__item`. Clicking an image opens it enlarged in a native
 * `<dialog>` lightbox with prev/next arrows, a counter and Esc/backdrop to
 * close.
 *
 * HISTORY: this replaced the old desktop hover-zoom (background-image at
 * 200% following the pointer) on 06-09-2026 — reunión Desmarq 04-09, min
 * 26:02. The gallery images carry printed captions inside, and the hover
 * zoom hid the original image (opacity 0) so the caption became unreadable
 * the moment you tried to look closer. Carlos: «lo ideal sería que pulses y
 * se abra». No styling decisions are made here.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} lightbox
 * @property {HTMLImageElement} image
 * @property {HTMLButtonElement} [closeButton]
 * @property {HTMLButtonElement} [prevButton]
 * @property {HTMLButtonElement} [nextButton]
 * @property {HTMLElement} [counter]
 *
 * @extends {Component<Refs>}
 */
export class PpzProductGallery extends Component {
  requiredRefs = ['lightbox', 'image'];

  #controller = new AbortController();
  /** @type {{src: string, alt: string}[]} */
  #items = [];
  #index = 0;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const buttons = [...this.querySelectorAll('.ppz-gallery-open')];

    this.#items = buttons.map((button) => ({
      src: button.dataset.largeSrc || '',
      alt: button.dataset.alt || '',
    }));

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => this.#open(index), { signal });
    });

    const { lightbox } = this.refs;
    lightbox.addEventListener('click', this.#handleBackdropClick, { signal });
    lightbox.addEventListener('keydown', this.#handleKeydown, { signal });
    this.refs.closeButton?.addEventListener('click', () => lightbox.close(), { signal });
    this.refs.prevButton?.addEventListener('click', () => this.#step(-1), { signal });
    this.refs.nextButton?.addEventListener('click', () => this.#step(1), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /** @param {number} index */
  #open(index) {
    this.#show(index);
    this.refs.lightbox.showModal();
  }

  /** @param {number} index */
  #show(index) {
    const count = this.#items.length;
    if (count === 0) return;

    this.#index = (index + count) % count;
    const item = this.#items[this.#index];

    this.refs.image.src = item.src;
    this.refs.image.alt = item.alt;

    if (this.refs.counter) {
      this.refs.counter.textContent = `${this.#index + 1} / ${count}`;
    }
  }

  /** @param {number} delta */
  #step = (delta) => this.#show(this.#index + delta);

  /**
   * The dialog fills the viewport with a transparent background, so a click
   * whose target is the dialog itself means "outside the image": close.
   * @param {MouseEvent} event
   */
  #handleBackdropClick = (event) => {
    if (event.target === this.refs.lightbox) this.refs.lightbox.close();
  };

  /** @param {KeyboardEvent} event — Esc lo gestiona el propio <dialog> */
  #handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') this.#step(-1);
    else if (event.key === 'ArrowRight') this.#step(1);
  };
}

if (!customElements.get('ppz-product-gallery')) {
  customElements.define('ppz-product-gallery', PpzProductGallery);
}
